/**
 * SummaryScreen (Screen 8)
 * 
 * "You already know vocab matters."
 * High-fidelity summary screen matching the reference image.
 * Fully adaptive across all iPhone sizes.
 * Dynamically updates content based on user's previous selections.
 */
import React from 'react';
import { useOnboardingViewport } from '../shared/useViewport';
import { useOnboarding } from '../../../context/OnboardingContext';

const SummaryItem = ({ icon, text, accent, vp }) => (
  <div style={{
    display: 'flex',
    alignItems: 'center',
    gap: '16px',
    padding: `${vp.sp(8, 10, 12)}px 0`
  }}>
    <div style={{
      width: `${vp.sp(36, 40, 44)}px`,
      height: `${vp.sp(36, 40, 44)}px`,
      borderRadius: '50%',
      background: '#F0FDFA',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      flexShrink: 0
    }}>
      {icon}
    </div>
    <div style={{ 
      fontSize: `${vp.sp(15, 16, 17)}px`, 
      fontWeight: '700', 
      color: '#0F172A',
      textAlign: 'left'
    }}>
      {accent ? <><span style={{ color: '#14B8A6' }}>{accent}</span> {text}</> : text}
    </div>
  </div>
);

export const SummaryScreen = ({ onNext, onBack }) => {
  const vp = useOnboardingViewport();
  const { goals = [], commitment = '5m' } = useOnboarding();

  // Dynamic Logic for Vocabulary Line
  const hasGre = goals.includes('gre');
  const hasGmat = goals.includes('gmat');
  const hasGeneral = goals.includes('general');

  let vocabLine = "General vocabulary improvement.";
  if (hasGre && hasGmat) {
    vocabLine = "GRE and GMAT vocabulary.";
  } else if (hasGre) {
    vocabLine = "GRE vocabulary.";
  } else if (hasGmat) {
    vocabLine = "GMAT vocabulary.";
  } else if (hasGeneral) {
    vocabLine = "General vocabulary improvement.";
  }

  // Dynamic Logic for Commitment Line
  const commitmentLabels = {
    '5m': '5-minute',
    '10m': '10-minute',
    '15m': '15-minute',
    '20m': '20-minute'
  };
  const commitmentAccent = commitmentLabels[commitment] || '5-minute';

  const primaryCTAStyle = {
    width: "100%",
    height: `${vp.spacing.ctaHeight}px`,
    background: "#14B8A6",
    borderRadius: `${vp.spacing.ctaBorderRadius}px`,
    border: "none",
    boxShadow: "0 8px 16px rgba(20, 184, 166, 0.2)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: "10px",
    color: "white",
    fontSize: `${vp.spacing.ctaFontSize}px`,
    fontWeight: "900",
    cursor: "pointer",
    transition: "transform 0.2s",
  };

  const isDesktop = vp.isTablet;

  return (
    <div style={{
      display: 'flex',
      flexDirection: 'column',
      flex: 1,
      width: '100%',
      maxWidth: isDesktop ? '480px' : '100%',
      margin: isDesktop ? '0 auto' : undefined,
      animation: 'fadeIn 0.6s ease-out',
      position: 'relative',
      background: 'transparent'
    }}>
      {/* 1. Decorative Sparkles */}
      <div style={{ position: 'absolute', top: '2%', left: '5%', color: '#FBBC05', fontSize: '20px' }}>✦</div>
      <div style={{ position: 'absolute', top: '5%', right: '8%', color: '#2DD4BF', fontSize: '18px' }}>✦</div>

      {/* 2. Headline Area */}
      <div style={{ textAlign: 'center', marginTop: '-10px' }}>
        <h2 style={{
          fontSize: vp.headlineFontSize,
          fontWeight: '900',
          color: '#0F172A',
          lineHeight: '1.1',
          margin: '0',
          letterSpacing: '-1.5px'
        }}>
          You already know<br />
          <span style={{ color: '#14B8A6', position: 'relative' }}>
            vocab matters.
            <svg style={{ position: 'absolute', bottom: '-8px', left: '0', width: '100%' }} height="8" viewBox="0 0 100 8" preserveAspectRatio="none">
               <path d="M0 5C30 2 70 2 100 5" stroke="#14B8A6" strokeWidth="2" fill="none" strokeLinecap="round" opacity="0.4" />
            </svg>
          </span>
        </h2>
        
        <p style={{
          fontSize: '15px',
          color: '#1E293B',
          lineHeight: '1.4',
          marginTop: `${vp.sp(14, 18, 22)}px`,
          fontWeight: '600',
          maxWidth: '300px',
          margin: `${vp.sp(14, 18, 22)}px auto 0`
        }}>
          The hard part is <span style={{ background: '#FEF3C7', padding: '0 4px', borderRadius: '4px' }}>staying consistent</span> with <span style={{ background: '#FEF3C7', padding: '0 4px', borderRadius: '4px' }}>boring word lists</span> long enough for the words to actually stick.
        </p>
      </div>

      {/* 3. Divider */}
      <div style={{ 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'center', 
        gap: '12px', 
        marginTop: `${vp.sp(20, 26, 32)}px`,
        opacity: 0.5
      }}>
        <div style={{ width: '16px', height: '1.5px', background: '#007982' }} />
        <span style={{ fontSize: '11px', fontWeight: '800', color: '#007982', letterSpacing: '2px' }}>GOALS SET</span>
        <div style={{ width: '16px', height: '1.5px', background: '#007982' }} />
      </div>

      {/* 4. Summary Card */}
      <div style={{
        background: 'white',
        borderRadius: '24px',
        padding: `${vp.sp(8, 12, 16)}px 24px`,
        marginTop: `${vp.sp(8, 10, 12)}px`,
        boxShadow: '0 10px 30px rgba(0,0,0,0.04)',
        border: '1px solid rgba(0,0,0,0.02)'
      }}>
        <SummaryItem 
          vp={vp}
          icon={<svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#14B8A6" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M22 10v6M2 10l10-5 10 5-10 5z"/><path d="M6 12v5c0 2 2 3 6 3s6-1 6-3v-5"/></svg>}
          text={vocabLine}
        />
        <SummaryItem 
          vp={vp}
          icon={<svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#14B8A6" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>}
          accent={commitmentAccent}
          text="daily practice."
        />
        <SummaryItem 
          vp={vp}
          icon={<svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#14B8A6" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>}
          text="Ready when you are."
        />
      </div>

      {/* 5. Mascot Area */}
      <div style={{
        flex: 1,
        position: 'relative',
        width: '100%',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        padding: '10px 0'
      }}>
        {/* Mascot Sparkles */}
        <div style={{ position: 'absolute', bottom: '25%', left: '10%', color: '#FBBC05', fontSize: '16px' }}>✦</div>
        <div style={{ position: 'absolute', top: '20%', right: '12%', color: '#2DD4BF', fontSize: '18px' }}>✦</div>

        <img 
          src="/images/onboarding/page7.webp"
          alt="Mascot"
          style={{
            width: '100%',
            maxWidth: `${vp.sp(220, 260, 300)}px`,
            height: 'auto',
            objectFit: 'contain',
            display: 'block'
          }}
        />
      </div>

      {/* 6. CTA Button */}
      <div style={{
        paddingTop: '10px',
        zIndex: 10
      }}>
        <button
          onClick={onNext}
          style={primaryCTAStyle}
        >
          I'm Ready to Make Progress
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
            <path d="M5 12h14M12 5l7 7-7 7" />
          </svg>
        </button>
      </div>
    </div>
  );
};

export default SummaryScreen;
